import { useEffect, useRef, useState } from "react";
import { Loader2, Save, Upload, Globe, Facebook, Youtube, Church, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
);
const inp = "bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40";

function Field({ id, label, value, onChange, placeholder, icon: Icon, className = "", ...rest }) {
  return (
    <div className={"space-y-1.5 " + className}>
      <Label htmlFor={id} className="text-gray-300 text-sm flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5 text-gray-500" />}{label}</Label>
      <Input id={id} data-testid={`profile-${id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inp} {...rest} />
    </div>
  );
}

// Shared editor. `base` = API path (/profile or /admin/churches/{id}/profile); `logoPath` = upload endpoint.
export function ChurchProfileForm({ base, logoPath, admin = false, onSaved, compact = false }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setF(null); api.get(base).then((r) => setF(r.data)).catch(() => toast.error("Couldn't load profile")); }, [base]);

  if (!f) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>;
  const set = (k) => (v) => setF({ ...f, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { email, user_id, ...body } = f;
      const r = await api.put(base, body);
      setF(r.data); toast.success("Profile saved"); onSaved?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save profile"); } finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(logoPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((x) => ({ ...x, logo_url: r.data.logo_url })); toast.success("Logo uploaded"); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Logo upload failed"); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const grid = compact ? "grid gap-3" : "grid gap-4 sm:grid-cols-2";
  const logoSrc = f.logo_url?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${f.logo_url}` : f.logo_url;
  return (
    <div data-testid="church-profile-form" className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-xl bg-gray-950 border border-gray-800 flex items-center justify-center overflow-hidden shrink-0">
          {f.logo_url ? <img data-testid="profile-logo-preview" src={logoSrc} alt="Church logo" className="h-full w-full object-contain" /> : <Church className="h-7 w-7 text-gray-600" />}
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          <button data-testid="profile-logo-upload" type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="text-sm px-3 py-1.5 rounded-lg border border-gray-700 text-gray-200 hover:border-amber-500/50 inline-flex items-center gap-2 disabled:opacity-60">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {f.logo_url ? "Replace logo" : "Upload logo"}
          </button>
          <p className="text-[11px] text-gray-500 mt-1">PNG, JPG, WebP or GIF, up to 5 MB. Also used on your players.</p>
        </div>
      </div>

      <section className="space-y-3">
        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Church & contact</p>
        <div className={grid}>
          <Field id="organization" label="Church name" value={f.organization} onChange={set("organization")} placeholder="Grace Community Church" />
          <Field id="name" label="Contact name" value={f.name} onChange={set("name")} placeholder="Pastor Jane Smith" />
          <Field id="phone" label="Phone number" value={f.phone} onChange={set("phone")} placeholder="(555) 123-4567" type="tel" />
          <Field id="website" label="Website" icon={Globe} value={f.website} onChange={set("website")} placeholder="https://yourchurch.org" />
        </div>
      </section>

      <section className="space-y-3">
        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Mailing address</p>
        <Field id="address" label="Street address" value={f.address} onChange={set("address")} placeholder="123 Main Street" />
        <div className={compact ? "grid gap-3 grid-cols-3" : "grid gap-4 grid-cols-3"}>
          <Field id="city" label="City" value={f.city} onChange={set("city")} />
          <Field id="state" label="State" value={f.state} onChange={set("state")} />
          <Field id="zip" label="ZIP" value={f.zip} onChange={set("zip")} />
        </div>
      </section>

      <section className="space-y-3">
        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Social media</p>
        <div className={compact ? "grid gap-3" : "grid gap-4 sm:grid-cols-3"}>
          <Field id="social_facebook" label="Facebook" icon={Facebook} value={f.social_facebook} onChange={set("social_facebook")} placeholder="facebook.com/yourchurch" />
          <Field id="social_youtube" label="YouTube" icon={Youtube} value={f.social_youtube} onChange={set("social_youtube")} placeholder="youtube.com/@yourchurch" />
          <Field id="social_x" label="X (Twitter)" icon={XLogo} value={f.social_x} onChange={set("social_x")} placeholder="x.com/yourchurch" />
        </div>
      </section>

      <section className="space-y-1.5">
        <Label className="text-gray-300 text-sm">{admin ? "Church's notes" : "Notes"}</Label>
        <Textarea data-testid="profile-church_notes" value={f.church_notes || ""} onChange={(e) => set("church_notes")(e.target.value)} rows={3} maxLength={2000} placeholder="Service times, denomination, anything StreamWorship should know…" className={inp + " resize-none"} />
      </section>

      {admin && (
        <section className="space-y-1.5 rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-3">
          <Label className="text-amber-300 text-sm flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Private admin notes</Label>
          <Textarea data-testid="profile-admin_notes" value={f.admin_notes || ""} onChange={(e) => set("admin_notes")(e.target.value)} rows={3} maxLength={4000} placeholder="Only admins can see this." className={inp + " resize-none"} />
        </section>
      )}

      <div className="flex justify-end">
        <button data-testid="profile-save" onClick={save} disabled={saving} className="gold-gradient text-black font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save profile
        </button>
      </div>
    </div>
  );
}
